<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNavigation } from '@/composables/useNavigation'
import { useMobileMenu } from '@/composables/useMobileMenu'
import {
  LayoutDashboard, Ticket, FolderKanban, ShoppingCart, FileBarChart,
  Download, Building2, Users, Settings, Activity, Package, LogOut,
  Shield, ShieldAlert, KeyRound, Calendar, Briefcase, FileEdit,
  ListTodo, LayoutGrid, Cog, Headset, Wrench, CreditCard, BookOpen,
} from 'lucide-vue-next'
import NavRail from './NavRail.vue'
import NavPanel from './NavPanel.vue'
import type { Pillar, NavItem } from '@/composables/useNavigation'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { isMobile, mobileMenuOpen, closeMenu } = useMobileMenu()
const { activePillarId, isPanelOpen, selectPillar, closePanel, openPanel, syncPillarToRoute } = useNavigation()

// Hover behavior — open panel when hovering the rail, close when leaving the entire nav
let closeTimeout: ReturnType<typeof setTimeout> | null = null

function handleNavMouseEnter() {
  if (closeTimeout) { clearTimeout(closeTimeout); closeTimeout = null }
  openPanel()
}

function handleNavMouseLeave() {
  // Small delay to prevent flickering when moving between rail and panel
  closeTimeout = setTimeout(() => closePanel(), 200)
}

// ==========================================
// STAFF PILLARS
// ==========================================
const staffPillars = computed((): Pillar[] => {
  const filterItems = (items: NavItem[]) => items.filter(item => {
    if (item.permission && !authStore.hasPermission(item.permission)) return false
    if (item.superAdminOnly && !authStore.isSuperAdmin) return false
    return true
  })

  return [
    {
      id: 'home',
      label: 'Workspace',
      icon: LayoutGrid,
      items: filterItems([
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'My Work', path: '/my-work', icon: Briefcase, badge: 'todo' },
      ]),
    },
    {
      id: 'service-desk',
      label: 'Service Desk',
      icon: Headset,
      items: filterItems([
        { name: 'Tickets', path: '/tickets', icon: Ticket, permission: 'tickets.view', badge: 'ticket' },
        { name: 'Todos', path: '/todos', icon: ListTodo, permission: 'todos.view' },
        { name: 'Knowledge Base', path: '/knowledge-base', icon: BookOpen, permission: 'kb.view' },
        { name: 'Calendar', path: '/calendar', icon: Calendar, permission: 'calendar.view' },
      ]),
    },
    {
      id: 'projects',
      label: 'Project Management',
      icon: FolderKanban,
      items: filterItems([
        { name: 'Projects', path: '/projects', icon: FolderKanban, permission: 'projects.view' },
        { name: 'Change Requests', path: '/change-requests', icon: FileEdit, permission: 'change_requests.view' },
      ]),
    },
    {
      id: 'operations',
      label: 'Operations',
      icon: Wrench,
      items: filterItems([
        { name: 'Procurement', path: '/procurement', icon: ShoppingCart },
        { name: 'Assets', path: '/assets', icon: Package },
        { name: 'Downloads', path: '/downloads', icon: Download },
        { name: 'Reports', path: '/reports', icon: FileBarChart, permission: 'reports.view' },
        { name: 'Billing', path: '/billing', icon: CreditCard, superAdminOnly: true },
      ]),
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      items: filterItems([
        { name: 'Password Manager', path: '/passwords', icon: KeyRound },
        { name: 'Browser Security', path: '/browser-security', icon: ShieldAlert },
        { name: 'M365 Alerts', path: '/m365-alerts', icon: ShieldAlert, permission: 'm365_alerts.view' },
        { name: 'Security Posture', path: '/security/posture', icon: Shield, permission: 'security.view' },
      ]),
    },
    {
      id: 'admin',
      label: 'Administration',
      icon: Settings,
      items: filterItems([
        { name: 'Clients', path: '/clients', icon: Building2 },
        { name: 'Users', path: '/users', icon: Users, permission: 'users.view' },
        { name: 'Configuration', path: '/configuration', icon: Cog },
        { name: 'Activity', path: '/activity', icon: Activity },
      ]),
    },
  ].filter(p => p.items.length > 0)
})

// ==========================================
// CLIENT PILLARS
// ==========================================
const clientPillars = computed((): Pillar[] => {
  const isEmployee = authStore.isClientEmployee
  const isClientAdminUser = authStore.isClientAdmin

  const filterItems = (items: NavItem[]) => items.filter(item => {
    if (item.hideForEmployee && isEmployee) return false
    if (item.clientAdminOnly && !isClientAdminUser) return false
    if (item.permission && !authStore.hasPermission(item.permission)) return false
    if (item.portalFeature && !authStore.hasPortalFeature(item.portalFeature)) return false
    return true
  })

  // Dashboard path varies by client role
  const dashboardPath = isClientAdminUser ? '/client/dashboard' : '/dashboard/personal'

  return [
    {
      id: 'home',
      label: 'Home',
      icon: LayoutGrid,
      items: filterItems([
        { name: 'Dashboard', path: dashboardPath, icon: LayoutDashboard },
      ]),
    },
    {
      id: 'support',
      label: 'Support',
      icon: Headset,
      items: filterItems([
        { name: 'My Tickets', path: '/tickets', icon: Ticket, permission: 'tickets.view' },
        { name: 'Service Catalog', path: '/client/services', icon: LayoutGrid, portalFeature: 'service_catalog' },
        { name: 'Knowledge Base', path: '/knowledge-base', icon: BookOpen, permission: 'kb.view' },
        { name: 'Downloads', path: '/downloads', icon: Download },
      ]),
    },
    {
      id: 'services',
      label: 'Services',
      icon: FolderKanban,
      items: filterItems([
        { name: 'Assets', path: '/assets', icon: Package, clientAdminOnly: true, portalFeature: 'assets' },
        { name: 'Projects', path: '/client/projects', icon: FolderKanban, hideForEmployee: true, portalFeature: 'projects' },
        { name: 'Team', path: '/client/team', icon: Users, clientAdminOnly: true },
        { name: 'Reports', path: '/reports', icon: FileBarChart, permission: 'reports.view', clientAdminOnly: true, portalFeature: 'reports' },
      ]),
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      items: filterItems([
        { name: 'Password Manager', path: '/passwords', icon: KeyRound, portalFeature: 'vault' },
        { name: 'Browser Security', path: '/browser-security', icon: ShieldAlert, clientAdminOnly: true, portalFeature: 'browser_security' },
      ]),
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: CreditCard,
      items: filterItems([
        { name: 'Invoices', path: '/billing', icon: CreditCard, clientAdminOnly: true, portalFeature: 'billing' },
      ]),
    },
  ].filter(p => p.items.length > 0)
})

const pillars = computed(() => authStore.isStaff ? staffPillars.value : clientPillars.value)

const activePillar = computed(() =>
  pillars.value.find(p => p.id === activePillarId.value) || pillars.value[0] || null
)

// Auto-sync pillar on route change, and close mobile menu
watch(() => route.path, () => {
  syncPillarToRoute(pillars.value)
  if (isMobile.value) closeMenu()
})

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}
</script>

<template>
  <aside
    class="nav-container"
    :class="{ 'mobile-open': isMobile && mobileMenuOpen }"
    @mouseenter="handleNavMouseEnter"
    @mouseleave="handleNavMouseLeave"
  >
    <NavRail
      :pillars="pillars"
      :active-pillar-id="activePillarId"
      :is-panel-open="isPanelOpen"
      @select-pillar="selectPillar"
      @logout="handleLogout"
    />
    <NavPanel
      :pillar="activePillar"
      :show="isPanelOpen"
    />
  </aside>
</template>
